// Inbox — the agency's notification feed + per-user channel preferences.
// Two tabs: Notifications (list, mark read / mark all read) and Preferences (per-type in-app +
// email toggles that round-trip). Agency-side only; the API is session-authed.
import { useEffect, useState } from 'react';
import { agencyApi, type InboxNotification, type NotificationPref } from '../../lib/agencyApi';
import { Button, Card, EmptyNote, ErrorNote, SectionHeader, Spinner } from './ui';
import { timeAgo } from '../../lib/time';

const TYPE_ICON: Record<string, string> = {
  'content.review_needed': '📝',
  'content.updated': '♻️',
  'render.failed': '⚠️',
  system: '📣',
};

// (A private `timeAgo` lived here — correct, but the fifth copy of the same helper. Now imported.)

export default function Inbox({ onUnreadChange }: { onUnreadChange?: (n: number) => void }) {
  const [tab, setTab] = useState<'feed' | 'prefs'>('feed');
  return (
    <div className="space-y-5">
      <div className="flex w-fit rounded-lg bg-slate-100 p-0.5 text-sm">
        <button className={`rounded-md px-3 py-1.5 ${tab === 'feed' ? 'bg-white font-medium shadow-sm' : 'text-slate-500'}`} onClick={() => setTab('feed')}>Notifications</button>
        <button className={`rounded-md px-3 py-1.5 ${tab === 'prefs' ? 'bg-white font-medium shadow-sm' : 'text-slate-500'}`} onClick={() => setTab('prefs')}>Preferences</button>
      </div>
      {tab === 'feed' ? <Feed onUnreadChange={onUnreadChange} /> : <Prefs />}
    </div>
  );
}

function Feed({ onUnreadChange }: { onUnreadChange?: (n: number) => void }) {
  const [data, setData] = useState<{ unread: number; notifications: InboxNotification[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const d = await agencyApi.inbox({ limit: 100 });
      setData(d);
      onUnreadChange?.(d.unread);
    } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void load(); }, []);

  async function markAll() {
    setBusy(true);
    try { await agencyApi.markRead(); await load(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  }
  async function markOne(id: string) {
    try { await agencyApi.markRead([id]); await load(); } catch (e) { setError((e as Error).message); }
  }

  if (error) return <ErrorNote error={error} />;
  if (!data) return <Spinner />;

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Notifications"
        subtitle="Content reviews, render alerts, and platform announcements for your agency."
        action={data.unread > 0 ? <Button variant="ghost" onClick={markAll} disabled={busy}>{busy ? 'Marking…' : `Mark all read (${data.unread})`}</Button> : undefined}
      />
      {data.notifications.length === 0 ? (
        <EmptyNote>You're all caught up — no notifications yet.</EmptyNote>
      ) : (
        <div className="space-y-2">
          {data.notifications.map((n) => (
            <Card key={n.id}>
              <div className="flex items-start gap-3">
                <span aria-hidden className="mt-0.5 text-lg">{TYPE_ICON[n.type] ?? '•'}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-brand" aria-label="Unread" />}
                    <span className={`text-sm ${n.read ? 'text-slate-600' : 'font-semibold text-slate-900'}`}>{n.title}</span>
                    <span className="ml-auto shrink-0 text-xs text-slate-400">{timeAgo(n.created_at)}</span>
                  </div>
                  {n.body && <p className="mt-0.5 text-sm text-slate-500">{n.body}</p>}
                </div>
                {!n.read && <button onClick={() => markOne(n.id)} className="shrink-0 text-xs text-slate-400 hover:text-slate-700">Mark read</button>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Prefs() {
  const [prefs, setPrefs] = useState<NotificationPref[] | null>(null);
  const [initial, setInitial] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    agencyApi.inboxPrefs()
      .then((r) => { setPrefs(r.prefs); setInitial(JSON.stringify(r.prefs)); })
      .catch((e) => setError((e as Error).message));
  }, []);

  function toggle(type: string, channel: 'inapp' | 'email') {
    setPrefs((prev) => (prev ?? []).map((p) => (p.type === type ? { ...p, [channel]: !p[channel] } : p)));
    setSaved(false);
  }
  async function save() {
    if (!prefs) return;
    setSaving(true); setError(null);
    try {
      const r = await agencyApi.saveInboxPrefs(prefs.map((p) => ({ type: p.type, inapp: p.inapp, email: p.email })));
      setPrefs(r.prefs); setInitial(JSON.stringify(r.prefs)); setSaved(true);
    } catch (e) { setError((e as Error).message); } finally { setSaving(false); }
  }

  if (error) return <ErrorNote error={error} />;
  if (!prefs) return <Spinner />;
  const dirty = JSON.stringify(prefs) !== initial;

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Notification preferences"
        subtitle="Choose how you're notified for each type of event. In-app shows in this inbox; email sends to your account address."
        action={
          <span className="flex items-center gap-2">
            {saved && !dirty && <span className="text-xs text-emerald-600">Saved</span>}
            <Button onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save preferences'}</Button>
          </span>
        }
      />
      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wide text-slate-400">
              <th className="py-2 text-left font-semibold">Event</th>
              <th className="w-20 py-2 text-center font-semibold">In-app</th>
              <th className="w-20 py-2 text-center font-semibold">Email</th>
            </tr>
          </thead>
          <tbody>
            {prefs.map((p) => (
              <tr key={p.type} className="border-b border-slate-50 last:border-0">
                <td className="py-3 pr-3 text-slate-700">{p.label}</td>
                <td className="py-3 text-center"><Toggle on={p.inapp} onClick={() => toggle(p.type, 'inapp')} /></td>
                <td className="py-3 text-center"><Toggle on={p.email} onClick={() => toggle(p.type, 'email')} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button
      role="switch"
      aria-checked={on}
      onClick={onClick}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${on ? 'bg-brand' : 'bg-slate-200'}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}
